import React, { useState, useEffect } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { motion } from 'framer-motion';
import { FadeIn } from '../components/animations';
import { isDemoMode, demoQuotesStore, ensureDemoSoldQuotes } from '../utils/demo';
import { ServiceFactory } from '../services/factory/ServiceFactory';

interface Quote {
  submissionId: string;
  companyName: string;
  transperraRep: string;
  ichraEffectiveDate: string;
  pepm: string;
  status: string;
  brokerName: string;
  employerName: string;
  brokerId: string;
  employerId: string;
  agency?: string;
  s3Key: string;
}

const EmployerQuotes: React.FC = () => {
  const { employerId } = useParams<{ employerId: string }>();
  const [searchParams] = useSearchParams();
  const brokerId = searchParams.get('brokerId');

  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [employerName, setEmployerName] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [updatingStatusId, setUpdatingStatusId] = useState<string | null>(null);
  const { } = useAuth();

  const fetchQuotes = React.useCallback(async () => {
    if (!employerId) return;

    setIsLoading(true);
    try {
      if (isDemoMode()) {
        // Use local demo data store for demo mode
        const localQuotes = demoQuotesStore.readAll();
        const soldQuotes = ensureDemoSoldQuotes();

        // Combine local and sold quotes, avoiding duplicates
        const allQuotes = [...soldQuotes, ...localQuotes].reduce((acc: Quote[], q: Quote) => {
          if (!acc.find((x: Quote) => x.submissionId === q.submissionId)) {
            // Filter out test data
            if ((q.companyName || '').toLowerCase() !== 'eggleston test' &&
                (q.employerName || '').toLowerCase() !== 'eggleston test') {
              acc.push(q);
            }
          }
          return acc;
        }, []);

        // Filter quotes for this specific employer
        const employerQuotes = allQuotes.filter(quote => quote.employerId === employerId);

        if (employerQuotes.length === 0) {
          setError('Employer not found or has no quotes');
          return;
        }

        setQuotes(employerQuotes);
        setEmployerName(employerQuotes[0].employerName || employerQuotes[0].companyName || employerId);
        setError(null);
        return;
      }

      // For production mode, use ServiceFactory to get quotes
      const apiService = ServiceFactory.createApiService();
      const allQuotes = await apiService.Quote.getQuotes();

      // Filter quotes for this specific employer
      const employerQuotes = allQuotes.filter((quote: any) =>
        (quote.employerId || quote.companyName) === employerId
      );

      if (employerQuotes.length === 0) {
        setError('Employer not found or has no quotes');
        return;
      }

      setQuotes(employerQuotes);
      setEmployerName(employerQuotes[0].employerName || employerQuotes[0].companyName || employerId);
      setError(null);
    } catch (err) {
      console.error('Fetch error:', err);
      setError(`Failed to fetch quotes: ${err instanceof Error ? err.message : 'Network error'}`);
    } finally {
      setIsLoading(false);
    }
  }, [employerId]);

  useEffect(() => {
    fetchQuotes();
  }, [fetchQuotes]);

  const handleStatusChange = async (quote: Quote, newStatus: string) => {
    if (quote.status === newStatus) return;

    setUpdatingStatusId(quote.submissionId);
    try {
      if (isDemoMode()) {
        // Update the status in demo store
        demoQuotesStore.updateStatus(quote.submissionId, newStatus);
        setQuotes(quotes.map(q => q.submissionId === quote.submissionId ? { ...q, status: newStatus } : q));
        return;
      }

      // For production mode, implement API call to update status
      // This would use ServiceFactory to update the quote status
      console.log('Status update not implemented for production mode');
    } catch (err) {
      console.error('Error updating status:', err);
      alert('Error updating status.');
    } finally {
      setUpdatingStatusId(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-seafoam border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 mx-auto px-2 py-8 w-full">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2 }}
        className="bg-brand-gradient rounded-brand p-6 mb-8 text-white shadow-brand"
      >
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold mb-2">Quotes for {employerName}</h1>
            <p className="text-sky-100">All quotes for this employer</p>
          </div>
          <div className="flex space-x-2">
            <Link
              to={`/employers/${employerId}?brokerId=${brokerId}`}
              className="bg-sky-100 hover:bg-sky-200 text-sky-800 px-4 py-2 rounded-md text-sm transition-colors duration-200"
            >
              Employer Details
            </Link>
            <Link
              to="/employers"
              className="bg-sky-100 hover:bg-sky-200 text-sky-800 px-4 py-2 rounded-md text-sm transition-colors duration-200"
            >
              ← All Employers
            </Link>
          </div>
        </div>
      </motion.div>

      {error && (
        <FadeIn>
          <div className="rounded-brand bg-red-50 dark:bg-red-900/20 p-4 text-sm text-red-700 dark:text-red-300 mb-6">
            <p className="flex items-start">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-red-500 mr-2 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              {error}
            </p>
            <Link
              to="/employers"
              className="mt-2 inline-block px-3 py-1 text-sm font-medium bg-red-100 text-red-700 hover:bg-red-200 dark:bg-red-900/30 dark:text-red-300 dark:hover:bg-red-900/50 rounded-md transition-colors duration-200"
            >
              Back to Employers
            </Link>
          </div>
        </FadeIn>
      )}

      {/* Quotes table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2, duration: 0.2 }}
        className="bg-white dark:bg-night-800 rounded-brand shadow-brand dark:shadow-dark overflow-auto w-full"
      >
        <div className="overflow-x-auto">
          {quotes.length > 0 ? (
            <table className="w-full divide-y divide-gray-200 dark:divide-night-700">
              <thead className="bg-gray-50 dark:bg-night-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Company
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    TPA Rep
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Effective Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    PEPM
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Broker
                  </th>
                  <th className="relative px-6 py-3">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-night-800 divide-y divide-gray-200 dark:divide-night-700">
                {quotes.map((quote, index) => (
                  <motion.tr
                    key={quote.submissionId}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.05 * index, duration: 0.3 }}
                    className="hover:bg-gray-50 dark:hover:bg-night-700 transition-colors duration-150"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Link
                        to={`/quotes/${quote.submissionId}?brokerId=${quote.brokerId}&employerId=${quote.employerId}`}
                        className="text-seafoam hover:text-seafoam-600 dark:text-sky dark:hover:text-sky-400 font-medium"
                      >
                        {quote.companyName || quote.employerName || quote.submissionId}
                      </Link>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {quote.transperraRep}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {quote.ichraEffectiveDate}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                      {quote.pepm}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <select
                        value={quote.status}
                        onChange={(e) => handleStatusChange(quote, e.target.value)}
                        disabled={updatingStatusId === quote.submissionId}
                        className={`px-2 py-1 text-xs font-semibold rounded-md border transition-colors duration-200 ${
                          updatingStatusId === quote.submissionId
                            ? 'bg-gray-100 text-gray-500 border-gray-300 cursor-wait'
                            : quote.status === 'Sold' || quote.status === 'Sold Case'
                              ? 'bg-green-100 text-green-800 border-green-200 dark:bg-green-900/20 dark:text-green-300 dark:border-green-800'
                              : 'bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-yellow-900/20 dark:text-yellow-300 dark:border-yellow-800'
                        }`}
                      >
                        <option value="Pending">Pending</option>
                        <option value="In Progress">In Progress</option>
                        <option value="Proposal Created">Proposal Created</option>
                        <option value="Sold Case">Sold Case</option>
                        <option value="Abandoned">Abandoned</option>
                      </select>
                      {updatingStatusId === quote.submissionId && (
                        <span className="ml-2 inline-flex items-center">
                          <svg className="animate-spin h-3 w-3 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {quote.brokerName}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <Link
                        to={`/quotes/${quote.submissionId}?brokerId=${quote.brokerId}&employerId=${quote.employerId}`}
                        className="bg-seafoam hover:bg-seafoam-600 text-white px-3 py-1 rounded-md text-sm transition-colors duration-200"
                      >
                        View Details
                      </Link>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="p-8 text-center">
              <div className="text-gray-500 dark:text-gray-400">
                <svg className="mx-auto h-12 w-12 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <p className="text-lg font-medium mb-2">No quotes found</p>
                <p className="text-sm">This employer has no quotes yet</p>
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default EmployerQuotes;